/**
 * AD-042 (2026-06-25) — L2D structured CONSTRUCTION DETAILS (11 groups).
 *
 * Replaces the single "Construction stage & details" textarea scaffold with the
 * deep technical capture the detailing team needs. UI contract:
 * `AVBP_L2D_VISTARA_FORM_MOCKUP_2026-06-25.html` §5.
 *
 * Groups: ① stage & structure · ② what's prepared (checklist) · ③ Water &
 * plumbing (+deeper spec) · ④ Electrical & power · ⑤ HVAC (+deeper spec) ·
 * ⑥ Ceiling & lighting · ⑦ Materials/finishes/MEP grade (+deeper spec) ·
 * ⑧ Doors & windows · ⑨ Soil & geology (+deeper spec) · ⑩ Local construction
 * technology · ⑪ Approvals/site/anything-specific.
 *
 * The "+ detailed spec" (criticality option) on Water/HVAC/Materials/Soil is a
 * simple optional notes field for v1 (the deeper sub-spec is staff-expanded only
 * when critical, per the mockup note).
 *
 * Stored as `answers.constructionDetails`. Owned by the now-REAL
 * `construction_details` module. Firebase-free + React-free (pure shape mapping).
 */

/* ─── option sets ─── */
export const DRAWING_STAGE_OPTS = ['Concept', 'Schematic', 'Developed design', 'GFC'] as const;
export const STRUCTURAL_SYSTEMS = ['RCC framed', 'Load-bearing masonry', 'Steel', 'Composite', 'Not decided'] as const;
export const PREPARED_CHECKS = [
  'Architectural', 'Structural', 'Electrical', 'Plumbing', 'HVAC',
  'Soil / geotech report', 'Material / finishes schedule', 'Door / window schedule', 'None yet',
] as const;
export const WATER_SOURCES = ['Municipal', 'Borewell', 'Tanker', 'Well', 'Multiple / other'] as const;
export const YES_NO = ['Yes', 'No'] as const;
export const ELEC_SOURCES = ['Grid', 'Grid + solar', 'Solar primary', 'Off-grid'] as const;
export const BACKUP_POWER = ['None', 'Inverter', 'DG set', 'Inverter + DG'] as const;
export const SOLAR_PROVISION = ['None', 'Roof-ready', 'Full install'] as const;
export const HVAC_APPROACH = ['Natural ventilation', 'Split AC (per room)', 'VRV / VRF', 'Central', 'Mixed'] as const;
export const FALSE_CEILING = ['None', 'All floors', 'Selected areas only', 'Optional — decide per room'] as const;
export const LIGHTING_APPROACH = ['Basic (general only)', 'Layered (general + task + accent)', 'Designer / mood', 'Smart / app-controlled'] as const;
export const SPEC_LEVEL = ['Standard', 'Premium', 'Luxury'] as const;
export const PLUMB_GRADE = ['Standard', 'Premium', 'Designer / imported'] as const;
export const ELEC_GRADE = ['Standard', 'Premium', 'Designer'] as const;
export const WINDOW_SYSTEM = ['uPVC', 'Aluminium', 'Wood', 'Steel', 'Mixed'] as const;
export const DOOR_TYPES = ['Flush', 'Panel', 'Solid wood', 'Mixed'] as const;
export const SOIL_TYPES = ['Not known', 'Black cotton', 'Red / lateritic', 'Sandy', 'Rocky', 'Filled / made-up'] as const;
export const GEOTECH = ['No', 'Yes — share', 'To be done'] as const;
export const WATER_TABLE = ['No concern', 'High water table', 'Flood-prone'] as const;
export const CONSTRUCTION_METHOD = ['Conventional RCC', 'Precast', 'Formwork (Mivan)', 'Local / vernacular', 'Not decided'] as const;
export const STATUTORY_APPROVAL = ['Sanctioned', 'Applied', 'Not yet', 'NA'] as const;
export const SITE_CONDITION = ['Vacant & ready', 'Existing to demolish', 'Under construction'] as const;
export const TARGET_START = ['Immediate', 'Within 3 months', '3–6 months', '6 months +'] as const;

/* ─── shape ─── */
export interface ConstructionDetailsInput {
  // ① stage & structure
  drawingStage: string;
  structuralSystem: string;
  floors: string;
  builtUpSqft: string;
  // ② what's prepared
  prepared: string[];
  // ③ water & plumbing
  waterSource: string;
  ugTank: string;
  ohTank: string;
  waterNotes: string; // + detailed spec
  // ④ electrical & power
  elecSource: string;
  backupPower: string;
  solarProvision: string;
  // ⑤ HVAC
  hvacApproach: string;
  hvacAreas: string;
  hvacNotes: string; // + detailed spec
  // ⑥ ceiling & lighting
  falseCeiling: string;
  lightingApproach: string;
  // ⑦ materials / finishes / MEP grade
  specLevel: string;
  plumbingGrade: string;
  electricalGrade: string;
  materialNotes: string; // + detailed spec
  // ⑧ doors & windows
  windowSystem: string;
  doorTypes: string;
  // ⑨ soil & geology
  soilType: string;
  geotech: string;
  waterTable: string;
  soilNotes: string; // + detailed spec
  // ⑩ local construction technology
  constructionMethod: string;
  localMaterialNotes: string;
  // ⑪ approvals / site / anything specific
  statutoryApproval: string;
  siteCondition: string;
  targetStart: string;
  specificNote: string;
}

export function blankConstructionDetails(): ConstructionDetailsInput {
  return {
    drawingStage: '', structuralSystem: '', floors: '', builtUpSqft: '',
    prepared: [],
    waterSource: '', ugTank: '', ohTank: '', waterNotes: '',
    elecSource: '', backupPower: '', solarProvision: '',
    hvacApproach: '', hvacAreas: '', hvacNotes: '',
    falseCeiling: '', lightingApproach: '',
    specLevel: '', plumbingGrade: '', electricalGrade: '', materialNotes: '',
    windowSystem: '', doorTypes: '',
    soilType: '', geotech: '', waterTable: '', soilNotes: '',
    constructionMethod: '', localMaterialNotes: '',
    statutoryApproval: '', siteCondition: '', targetStart: '', specificNote: '',
  };
}

function t(v: string | undefined): string { return (v || '').trim(); }
function asStr(v: unknown): string { return typeof v === 'string' ? v : v == null ? '' : String(v); }
function asStrArr(v: unknown): string[] { return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []; }

export function constructionHasContent(c: ConstructionDetailsInput): boolean {
  return Object.entries(c).some(([k, v]) =>
    k === 'prepared' ? (v as string[]).length > 0 : t(v as string),
  );
}

/** PURE — structured construction details → answers map (empty fields dropped). */
export function constructionToAnswer(c: ConstructionDetailsInput): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(c)) {
    if (k === 'prepared') { if ((v as string[]).length) out.prepared = v; continue; }
    const s = t(v as string);
    if (s) out[k] = s;
  }
  return out;
}

/** PURE — answers map → structured construction details (defensive). */
export function constructionFromAnswer(raw: unknown): ConstructionDetailsInput {
  const d = (raw || {}) as Record<string, unknown>;
  const blank = blankConstructionDetails();
  const out: ConstructionDetailsInput = { ...blank };
  for (const k of Object.keys(blank) as (keyof ConstructionDetailsInput)[]) {
    if (k === 'prepared') { out.prepared = asStrArr(d.prepared); continue; }
    (out[k] as string) = asStr(d[k]);
  }
  return out;
}
